import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { Bike, BikeCategory } from 'redux/bikeCatalog/bikeCatalogTypes'
import EvChargerIcon from 'core-system/Icons/Misc/EvCharger'
import { formatBikePrice, formatPostSubsidyPrice } from '../utils'

const Card = styled.div<{ $unavailable?: boolean }>`
  ${(props) => props.theme.baseCard};
  position: relative;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-height: 260px;
  opacity: ${(props) => (props.$unavailable ? 0.45 : 1)};
  filter: ${(props) => (props.$unavailable ? 'grayscale(1)' : 'none')};
`

const BikeImageFrame = styled.div<{
  $useImageBackground?: boolean
  $backgroundImage?: string
}>`
  width: 100%;
  height: 130px;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: transparent;
  background-image: ${(props) =>
    props.$useImageBackground && props.$backgroundImage
      ? `url(${props.$backgroundImage})`
      : 'none'};
  background-size: cover;
  background-position: center;
`

const BikeImage = styled.img`
  width: 100%;
  height: 130px;
  object-fit: contain;
  background: transparent;
`

const BikeMeta = styled(Text)`
  font-size: 0.75rem;
  color: ${palette.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`

const ElectricIconWrap = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  background: ${palette.secondary.purple100};
  border: 1px solid ${palette.secondary.purple300};
  svg {
    color: ${palette.secondary.purple700};
  }
`

const BikeActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.375rem;
  padding: 0 0.75rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid ${palette.secondary.purple700};
  background: ${palette.secondary.purple700};
  color: ${palette.white};
  font-weight: 600;
  width: fit-content;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

interface BikeCatalogCardProps {
  bike: Bike
  subsidyBudgetCents: number
  isVendorCheckoutLoading?: boolean
  onVendorCheckout: (bike: Bike) => void
}

const CATEGORY_LABELS: Record<BikeCategory | '', string> = {
  '': '',
  E_BIKE: 'E-Bike',
  PEDAL_BIKE: 'Pedal Bike',
  E_SCOOTER: 'E-Scooter',
}

const BikeCatalogCard = ({
  bike,
  subsidyBudgetCents,
  isVendorCheckoutLoading = false,
  onVendorCheckout,
}: BikeCatalogCardProps) => {
  const useImageBackground =
    bike.vendor === 'WOMBI' && bike.manufacturer === 'Tern'
  const categoryLabel = CATEGORY_LABELS[bike.category]
  const isElectric = bike.category === 'E_BIKE' || bike.category === 'E_SCOOTER'
  const isUnavailable = bike.isAvailable === false
  const shouldShowCheckout = Boolean(bike.productUrl)
  return (
    <Card $unavailable={isUnavailable} aria-disabled={isUnavailable}>
      <BikeImageFrame
        $useImageBackground={useImageBackground}
        $backgroundImage={bike.imageUrl || undefined}
      >
        {bike.imageUrl ? (
          <BikeImage src={bike.imageUrl} alt={bike.name} />
        ) : (
          <BikeImage
            src='data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
            alt={bike.name}
          />
        )}
      </BikeImageFrame>
      <BikeMeta variant='action4'>{bike.vendor}</BikeMeta>
      <TitleRow>
        <Text variant='h4'>{bike.name}</Text>
      </TitleRow>
      <Text variant='action3' textColor={palette.text.secondary}>
        {bike.manufacturer}
        {bike.vehicleType ? ` - ${bike.vehicleType}` : ''}
        {categoryLabel ? ` - ${categoryLabel}` : ''}
      </Text>
      <Text
        variant='action3'
        style={{ textDecoration: 'line-through' }}
      >
        {formatBikePrice(bike.monthlyPrice)}
      </Text>
      <Text
        variant='action2'
        textColor={palette.secondary.purple700}
        fontWeight='bold'
      >
        {formatPostSubsidyPrice(bike.monthlyPrice, subsidyBudgetCents)}
      </Text>
      <CardFooter>
        {isUnavailable ? (
          <Text variant='action3' textColor={palette.text.disabled}>
            Out of stock - check back soon
          </Text>
        ) : shouldShowCheckout ? (
          <BikeActionButton
            type='button'
            disabled={isVendorCheckoutLoading}
            onClick={() => onVendorCheckout(bike)}
          >
            {isVendorCheckoutLoading ? 'Opening checkout...' : 'Checkout'}
          </BikeActionButton>
        ) : null}
      </CardFooter>
      {isElectric && (
        <ElectricIconWrap aria-label='Electric'>
          <EvChargerIcon width={14} height={14} />
        </ElectricIconWrap>
      )}
    </Card>
  )
}

export default BikeCatalogCard
